import { ChevronsUpDown } from 'lucide-react'
import { ClickableRow } from '@/components/ui/ListTable'
import { useUpdateIssuePriority } from '../../hooks/useSystemIssues'
import type {
  SystemIssue,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../../types/system-issues'

export const STATUS_LABELS: Record<SystemIssueStatus, string> = {
  open: 'Aberto',
  pending: 'Pendente',
  in_progress: 'Em andamento',
  resolved: 'Resolvido',
}
export const STATUS_DOT_CLASS: Record<SystemIssueStatus, string> = {
  open: 'bg-green-500',
  pending: 'bg-yellow-400',
  in_progress: 'bg-[#2563eb]',
  // Só contorno: encerrado não compete visualmente com os status ativos.
  resolved: 'border-[1.5px] border-gray-400',
}

/** Mesmos 4 níveis da fila de Solicitações/Melhorias. "Média" fica neutra
 * (cinza), sem competir com as cores de status. */
export const PRIORITY_LABELS: Record<SystemIssuePriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}
const PRIORITY_DOT_CLASS: Record<SystemIssuePriority, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-slate-400',
  low: 'border-[1.5px] border-gray-400',
}
const PRIORITY_OPTIONS: SystemIssuePriority[] = [
  'urgent',
  'high',
  'medium',
  'low',
]

function formatDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function formatDateTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface IssueRowProps {
  issue: SystemIssue
  onSelect: (issueId: number) => void
  /**
   * Só a fila de triagem mostra quem reportou + a empresa — em
   * "Meus problemas" é sempre "eu mesmo" e o CC de quem está olhando, então
   * vira ruído em vez de informação. Controla se as colunas extras (e o
   * `<thead>` correspondente, montado por quem usa esta linha) aparecem.
   */
  showReporterMeta?: boolean
  /**
   * Coluna "Empresa". Default segue `showReporterMeta`; a aba HUB
   * passa `false` porque ali o CC é sempre o do processo aberto — coluna
   * redundante que só rouba largura do painel.
   */
  showCostCenter?: boolean
  /** Só a triagem (dev) pode mudar a prioridade depois de criada — em
   * "Meus problemas" a coluna aparece, mas só como badge de leitura. */
  canEditPriority?: boolean
}

/**
 * Linha de tabela compartilhada entre `MyIssuesList` (reporter) e
 * `TriageQueueList` (dev) — modo tabela (a pedido do usuário, substituindo
 * os cards anteriores): referência do processo vinculado sob o
 * título; atribuição, status, prioridade, data de abertura, data/hora da
 * última mensagem e badge de não lidas como colunas próprias (a última,
 * no fim da linha).
 */
export function IssueRow({
  issue,
  onSelect,
  showReporterMeta = false,
  showCostCenter = showReporterMeta,
  canEditPriority = false,
}: IssueRowProps) {
  const unread = issue.unread_count ?? 0
  const updatePriority = useUpdateIssuePriority(issue.id)
  const priorityDot = (
    <span
      className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT_CLASS[issue.priority]}`}
      aria-hidden
    />
  )

  return (
    // `ClickableRow` (não <button>): a linha contém o <select> de prioridade.
    <ClickableRow onClick={() => onSelect(issue.id)}>
      <td className="min-w-[400px] px-4 py-2.5">
        <div className="min-w-0">
          <div className="text-[11px] text-subtle">#{issue.id}</div>
          <div className="truncate font-medium text-body">{issue.title}</div>
          {issue.related_process?.process_number && (
            <div
              className="mt-1 text-[11px] font-medium text-body/80"
              title={`Processo ${issue.related_process.process_number}`}
            >
              {issue.related_process.process_number}
            </div>
          )}
        </div>
      </td>
      {showReporterMeta && (
        <td className="whitespace-nowrap px-4 py-2.5 text-body/80">
          {issue.created_by?.full_name ?? '—'}
        </td>
      )}
      {showCostCenter && (
        <td className="whitespace-nowrap px-4 py-2.5 text-body/80">
          {issue.company?.name ?? '—'}
        </td>
      )}
      <td className="whitespace-nowrap px-4 py-2.5 text-body/80">
        {issue.assigned_to?.full_name ?? 'Não atribuído'}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-body/80">
          <span
            className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[issue.status]}`}
            aria-hidden
          />
          {STATUS_LABELS[issue.status]}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-2.5">
        {canEditPriority ? (
          <div className="relative inline-flex items-center gap-1.5 pr-4 text-body/80">
            {priorityDot}
            <select
              value={issue.priority}
              disabled={updatePriority.isLoading}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation()
                updatePriority.mutate(e.target.value as SystemIssuePriority)
              }}
              className="cursor-pointer appearance-none bg-transparent outline-none disabled:cursor-wait"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
            <ChevronsUpDown
              className="pointer-events-none absolute right-0 h-2.5 w-2.5 opacity-60"
              aria-hidden
            />
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-body/80">
            {priorityDot}
            {PRIORITY_LABELS[issue.priority]}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-subtle">
        {formatDate(issue.created_at)}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-subtle">
        {formatDateTime(issue.last_message_at ?? issue.created_at)}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5">
        {unread > 0 && (
          <span
            className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold tabular-nums text-white"
            title={`${unread} mensage${unread === 1 ? 'm' : 'ns'} não lida${unread === 1 ? '' : 's'}`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </td>
    </ClickableRow>
  )
}
